import React, { useState, useEffect, useMemo } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  return isMobile
}

interface Client { id: number; name: string }

interface RequirementItem {
  id?: number
  name: string
  unit: string
  quantity: string
  unit_price: string
  total_price: string
  description: string
  category: string
}

interface RequirementData {
  client_id: number
  requirement_type: 'ZM' | 'ZU' | 'ZN'
  subject: string
  description: string
  priority: 'low' | 'medium' | 'high'
  notes: string
  items: RequirementItem[]
  relates_to_infrastructure: boolean
  infrastructure_object_id: number | null
}

interface RequirementsCreatorProps {
  token: string
  isOpen: boolean
  onClose: () => void
  onRequirementCreated: () => void
  requirementId?: string
}

// Trwały szkic zapisywany TYLKO po kliknięciu "Zapisz wersję roboczą"
const DRAFT_KEY = 'requirement_draft_v2'

export default function RequirementsCreator({
  token,
  isOpen,
  onClose,
  onRequirementCreated,
  requirementId
}: RequirementsCreatorProps) {
  const { showError, showSuccess, showConfirm } = useNotificationContext()
  const isMobile = useIsMobile()

  const [step, setStep] = useState<number>(1)
  const [clients, setClients] = useState<Client[]>([])
  const [infrastructureObjects, setInfrastructureObjects] = useState<{id: number, name: string}[]>([])
  const [loadingClients, setLoadingClients] = useState<boolean>(true)
  const [loadingInfrastructure, setLoadingInfrastructure] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [currentRequirementId, setCurrentRequirementId] = useState<string>('')

  const emptyData: RequirementData = useMemo(() => ({
    client_id: 0,
    requirement_type: 'ZM',
    subject: '',
    description: '',
    priority: 'medium',
    notes: '',
    items: [],
    relates_to_infrastructure: false,
    infrastructure_object_id: null
  }), [])

  const [form, setForm] = useState<RequirementData>(emptyData)
  const isEditMode = Boolean(requirementId)

  useEffect(() => {
    if (isOpen) {
      loadClients()
      if (requirementId) {
        loadRequirement(requirementId)
      } else {
        resetForm()
      }
    }
  }, [isOpen, requirementId])

  // Load infrastructure objects when client changes
  useEffect(() => {
    if (form.client_id) {
      loadInfrastructureObjects(form.client_id)
    }
  }, [form.client_id])

  const resetForm = () => {
    setForm({ ...emptyData })
    setStep(1)
    setCurrentRequirementId('')
  }

  const loadClients = async () => {
    try {
      setLoadingClients(true)
      const resp = await fetch('/api/clients/list', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error()
      const data: Client[] = await resp.json()
      setClients(data)
      if (!isEditMode && data.length > 0 && !form.client_id) {
        // Auto-select first client
        setForm(f => ({ ...f, client_id: data[0].id }))
      }
    } catch {
      showError('Błąd ładowania', 'Nie można pobrać listy klientów')
    } finally {
      setLoadingClients(false)
    }
  }

  const loadInfrastructureObjects = async (clientId: number) => {
    if (!clientId) return
    try {
      setLoadingInfrastructure(true)
      const resp = await fetch(`/api/infrastructure/client/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error()
      const data = await resp.json()
      setInfrastructureObjects(data)
    } catch {
      showError('Błąd ładowania', 'Nie można pobrać listy obiektów infrastruktury')
      setInfrastructureObjects([])
    } finally {
      setLoadingInfrastructure(false)
    }
  }

  const loadRequirement = async (id: string) => {
    try {
      const resp = await fetch(`/api/requirements/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error()
      const data = await resp.json()
      setCurrentRequirementId(data.id)
      const merged: RequirementData = {
        client_id: data.client_id ?? 0,
        requirement_type: (data.requirement_type ?? 'ZM') as 'ZM' | 'ZU' | 'ZN',
        subject: data.subject ?? '',
        description: data.description ?? '',
        priority: (data.priority ?? 'medium') as 'low' | 'medium' | 'high',
        notes: data.notes ?? '',
        items: Array.isArray(data.items) ? data.items : [],
        relates_to_infrastructure: Boolean(data.relates_to_infrastructure),
        infrastructure_object_id: data.infrastructure_object_id ?? null
      }
      setForm(merged)
    } catch {
      showError('Błąd', 'Nie udało się pobrać danych zapotrzebowania do edycji')
      onClose()
    }
  }

  const validate = (): string | null => {
    if (!form.client_id) return 'Wybierz klienta.'
    if (!form.subject.trim()) return 'Wprowadź temat zapotrzebowania.'
    return null
  }

  // Trwały zapis szkicu — tylko na wyraźne kliknięcie
  const handleSaveDraftClick = () => {
    if (isEditMode) {
      showError('Info', 'Wersja robocza (localStorage) dostępna tylko przy tworzeniu nowego zapotrzebowania.')
      return
    }
    localStorage.setItem(DRAFT_KEY, JSON.stringify(form))
    showSuccess('Zapisano', 'Wersja robocza zapisana lokalnie w przeglądarce.')
  }

  const clearDraft = async () => {
    if (isEditMode) return
    const ok = await showConfirm('Wyczyścić wersję roboczą?', 'Operacja usunie lokalny szkic.')
    if (!ok) return
    localStorage.removeItem(DRAFT_KEY)
    showSuccess('Wyczyszczono', 'Usunięto wersję roboczą.')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const calculateEstimatedCost = () => {
    let total = 0
    form.items.forEach(item => {
      const quantity = parseFloat(item.quantity) || 0
      const unitPrice = parseFloat(item.unit_price) || 0
      total += quantity * unitPrice
    })
    return total.toFixed(2)
  }

  // Zapis całości (kreator)
  const submitCreate = async () => {
    const err = validate()
    if (err) {
      showError('Błąd walidacji', err)
      return
    }
    setSaving(true)
    try {
      const estimatedCost = calculateEstimatedCost()

      const resp = await fetch('/api/requirements', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: form.client_id,
          requirement_type: form.requirement_type,
          subject: form.subject,
          description: form.description,
          priority: form.priority,
          notes: form.notes,
          estimated_cost: estimatedCost,
          relates_to_infrastructure: form.relates_to_infrastructure,
          infrastructure_object_id: form.infrastructure_object_id,
          items: form.items
        }),
      })
      if (!resp.ok) {
        let msg = 'Nie udało się zapisać zapotrzebowania.'
        try { const d = await resp.json(); msg = d.detail || msg } catch {}
        showError('Błąd', msg)
        return
      }
      const data = await resp.json()
      showSuccess('Sukces', `Zapotrzebowanie utworzone z ID: ${data.id}`)
      localStorage.removeItem(DRAFT_KEY)
      resetForm()
      onRequirementCreated()
      onClose()
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem.')
    } finally {
      setSaving(false)
    }
  }

  // Zapis w edycji (formularz zbiorczy)
  const submitEdit = async () => {
    const err = validate()
    if (err) {
      showError('Błąd walidacji', err)
      return
    }
    if (!requirementId) return
    setSaving(true)
    try {
      const estimatedCost = calculateEstimatedCost()

      const resp = await fetch(`/api/requirements/${requirementId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: form.client_id,
          requirement_type: form.requirement_type,
          subject: form.subject,
          description: form.description,
          priority: form.priority,
          notes: form.notes,
          estimated_cost: estimatedCost,
          relates_to_infrastructure: form.relates_to_infrastructure,
          infrastructure_object_id: form.infrastructure_object_id,
          items: form.items
        }),
      })
      if (!resp.ok) {
        let msg = 'Nie udało się zaktualizować zapotrzebowania.'
        try { const d = await resp.json(); msg = d.detail || msg } catch {}
        showError('Błąd', msg)
        return
      }

      showSuccess('Zapisano', 'Zapotrzebowanie zaktualizowano.')
      resetForm()
      onRequirementCreated()
      onClose()
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem.')
    } finally {
      setSaving(false)
    }
  }

  // ============ UI ============

  if (!isOpen) return null

  const ClientSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Klient *</label>
      {loadingClients ? (
        <div className="text-sm text-gray-600">Ładowanie…</div>
      ) : (
        <select
          value={form.client_id}
          onChange={(e) => setForm({
            ...form,
            client_id: parseInt(e.target.value),
            relates_to_infrastructure: false,
            infrastructure_object_id: null
          })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">— wybierz klienta —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
    </div>
  )

  const InfrastructureSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-3">Czy dotyczy obiektu infrastruktury?</label>
      <div className="flex gap-4 mb-3">
        <button
          type="button"
          onClick={() => setForm({
            ...form,
            relates_to_infrastructure: true,
            infrastructure_object_id: null
          })}
          className={`px-4 py-2 rounded-md border text-sm ${
            form.relates_to_infrastructure
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
          }`}
        >
          Tak
        </button>
        <button
          type="button"
          onClick={() => setForm({
            ...form,
            relates_to_infrastructure: false,
            infrastructure_object_id: null
          })}
          className={`px-4 py-2 rounded-md border text-sm ${
            !form.relates_to_infrastructure
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
          }`}
        >
          Nie
        </button>
      </div>

      {form.relates_to_infrastructure && (
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Wybierz obiekt infrastruktury</label>
          {loadingInfrastructure ? (
            <div className="text-sm text-gray-600">Ładowanie obiektów…</div>
          ) : infrastructureObjects.length > 0 ? (
            <select
              value={form.infrastructure_object_id ?? ''}
              onChange={(e) => setForm({
                ...form,
                infrastructure_object_id: e.target.value ? parseInt(e.target.value) : null
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— wybierz obiekt infrastruktury —</option>
              {infrastructureObjects.map(obj => (
                <option key={obj.id} value={obj.id}>{obj.name}</option>
              ))}
            </select>
          ) : (
            <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
              Brak obiektów infrastruktury dla wybranego klienta.
              <br />
              <a
                href="/infrastructure"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Przejdź do zarządzania obiektami infrastruktury →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  )

  const TypeSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Typ zapotrzebowania *</label>
      <div className={`${isMobile ? 'flex flex-col gap-2' : 'grid grid-cols-3 gap-2'}`}>
        <button
          type="button"
          onClick={() => setForm({ ...form, requirement_type: 'ZM' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.requirement_type === 'ZM' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Materiał
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, requirement_type: 'ZU' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.requirement_type === 'ZU' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Usługa
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, requirement_type: 'ZN' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.requirement_type === 'ZN' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Narzędzia
        </button>
      </div>
    </div>
  )

  const SubjectSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Temat zapotrzebowania *</label>
      <input
        type="text"
        value={form.subject}
        onChange={(e) => setForm({ ...form, subject: e.target.value })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Krótki opis przedmiotu zapotrzebowania…"
      />
    </div>
  )

  const PrioritySection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Priorytet</label>
      <div className={`${isMobile ? 'flex flex-col gap-2' : 'grid grid-cols-3 gap-2'}`}>
        <button
          type="button"
          onClick={() => setForm({ ...form, priority: 'low' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.priority === 'low' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Niski
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, priority: 'medium' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.priority === 'medium' ? 'bg-yellow-600 text-white border-yellow-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Średni
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, priority: 'high' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.priority === 'high' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Wysoki
        </button>
      </div>
    </div>
  )

  const DescriptionSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Opis zapotrzebowania</label>
      <textarea
        rows={4}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Szczegółowy opis zapotrzebowania…"
      />
    </div>
  )

  const addItem = () => {
    setForm({
      ...form,
      items: [
        ...form.items,
        {
          name: '',
          unit: '',
          quantity: '',
          unit_price: '',
          total_price: '',
          description: '',
          category: ''
        }
      ]
    })
  }

  const removeItem = (index: number) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    })
  }

  const updateItem = (index: number, field: keyof RequirementItem, value: string) => {
    const newItems = [...form.items]
    newItems[index] = { ...newItems[index], [field]: value }
    setForm({ ...form, items: newItems })
  }

  const calculateTotalPrice = (item: RequirementItem) => {
    const quantity = parseFloat(item.quantity) || 0
    const unitPrice = parseFloat(item.unit_price) || 0
    return (quantity * unitPrice).toFixed(2)
  }

  const autoCalculateTotalPrice = (index: number) => {
    const item = form.items[index]
    const totalPrice = calculateTotalPrice(item)
    updateItem(index, 'total_price', totalPrice)
  }

  const ItemsSection = (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Pozycje zapotrzebowania</h3>
        <button
          type="button"
          onClick={addItem}
          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm"
        >
          + Dodaj pozycję
        </button>
      </div>

      {form.items.map((item, index) => (
        <div key={index} className="border border-gray-200 rounded-md p-4 mb-4">
          <div className="flex justify-between items-center mb-3">
            <h5 className="text-sm font-medium text-gray-700">Pozycja {index + 1}</h5>
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="text-red-600 hover:text-red-800 text-sm"
            >
              Usuń
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nazwa pozycji</label>
              <input
                type="text"
                value={item.name}
                onChange={(e) => updateItem(index, 'name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nazwa pozycji..."
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Kategoria</label>
              <select
                value={item.category}
                onChange={(e) => updateItem(index, 'category', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Wybierz kategorię</option>
                <option value="Automatyka">Automatyka</option>
                <option value="Hydraulika">Hydraulika</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Ilość</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={item.quantity}
                onChange={(e) => {
                  const value = e.target.value
                  updateItem(index, 'quantity', value)
                  // Auto-calculate total price when both quantity and unit_price have valid values
                  if (value && !isNaN(parseFloat(value)) && item.unit_price && !isNaN(parseFloat(item.unit_price))) {
                    autoCalculateTotalPrice(index)
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                placeholder="1"
                style={{ appearance: 'textfield' }}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Jednostka</label>
              <input
                type="text"
                value={item.unit}
                onChange={(e) => updateItem(index, 'unit', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="szt."
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Cena jednostkowa</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={item.unit_price}
                onChange={(e) => {
                  const value = e.target.value
                  updateItem(index, 'unit_price', value)
                  // Auto-calculate total price when both quantity and unit_price have valid values
                  if (value && !isNaN(parseFloat(value)) && item.quantity && !isNaN(parseFloat(item.quantity))) {
                    autoCalculateTotalPrice(index)
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                placeholder="0.00"
                style={{ appearance: 'textfield' }}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Cena całkowita</label>
              <div className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm bg-gray-50">
                {calculateTotalPrice(item)} zł
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Opis pozycji</label>
              <input
                type="text"
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Opis pozycji..."
              />
            </div>
          </div>
        </div>
      ))}

      {form.items.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          Brak pozycji. Kliknij "Dodaj pozycję" aby dodać pierwszą pozycję.
        </div>
      )}

      {form.items.length > 0 && (
        <div className="mt-4 p-4 bg-blue-50 rounded-md">
          <div className="text-sm text-blue-800">
            <strong>Szacowany koszt całkowity:</strong> {calculateEstimatedCost()} zł
          </div>
        </div>
      )}
    </div>
  )

  const NotesSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Dodatkowe informacje</label>
      <textarea
        rows={3}
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Dodatkowe uwagi, wymagania, terminy…"
      />
    </div>
  )

  // ----- Tryb EDYCJI: formularz zbiorczy -----
  if (isEditMode) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Edycja zapotrzebowania {currentRequirementId}</h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" aria-label="Zamknij">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-180px)]">
            {ClientSection}
            {InfrastructureSection}
            {TypeSection}
            {SubjectSection}
            {PrioritySection}
            {DescriptionSection}
            {ItemsSection}
            {NotesSection}
          </div>

          <div className="flex justify-end gap-3 p-6 border-t">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Anuluj
            </button>
            <button
              onClick={submitEdit}
              disabled={saving || loadingClients}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ----- Tryb TWORZENIA: modal krokowy -----
  const renderStep = () => {
    switch (step) {
      case 1: return (<>{ClientSection}</>)
      case 2: return (<>{InfrastructureSection}</>)
      case 3: return (<>{TypeSection}</>)
      case 4: return (<>{SubjectSection}</>)
      case 5: return (<>{PrioritySection}</>)
      case 6: return (<>{DescriptionSection}</>)
      case 7: return (<>{ItemsSection}</>)
      case 8: return (<>{NotesSection}</>)
      default: return null
    }
  }

  const canGoNext = (): boolean => {
    if (step === 1) return Boolean(form.client_id)
    if (step === 2) return !form.relates_to_infrastructure || (form.relates_to_infrastructure && form.infrastructure_object_id !== null)
    if (step === 4) return form.subject.trim().length > 0
    return true
  }

  const getTotalSteps = (): number => {
    return 8
  }

  const next = () => setStep(Math.min(step + 1, getTotalSteps()))
  const prev = () => setStep(Math.max(1, step - 1))

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Nowe zapotrzebowanie</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" aria-label="Zamknij">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="text-sm text-gray-500 mb-3">Krok {step} z {getTotalSteps()}</div>
          {renderStep()}
        </div>

        {/* Footer */}
        <div className={`${isMobile ? 'flex flex-col gap-3 p-4' : 'flex items-center justify-between gap-2 p-6'} border-t`}>
          <div className={`${isMobile ? 'flex flex-col gap-2' : 'flex items-center gap-2'}`}>
            <button
              type="button"
              onClick={handleSaveDraftClick}
              className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'} font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md`}
            >
              Zapisz wersję roboczą
            </button>
            <button
              type="button"
              onClick={clearDraft}
              className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'} font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md`}
            >
              Wyczyść szkic
            </button>
          </div>

          <div className={`${isMobile ? 'flex justify-between gap-2' : 'flex items-center gap-2'}`}>
            <button
              onClick={prev}
              disabled={step === 1}
              className={`${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50`}
            >
              Wstecz
            </button>
            {step < getTotalSteps() ? (
              <button
                onClick={next}
                disabled={!canGoNext()}
                className={`${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50`}
              >
                Dalej
              </button>
            ) : (
              <button
                onClick={submitCreate}
                disabled={saving || loadingClients}
                className={`${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50`}
              >
                {saving ? 'Zapisywanie…' : 'Utwórz zapotrzebowanie'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
